import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Globe2, Image as ImageIcon, FileText, ArrowRight, History, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ICON = { web: Globe2, image: ImageIcon, document: FileText };
const TYPE_LABEL = { web: "Site internet", image: "Image", document: "Document" };

/** Génère la liste des numéros de page à afficher avec ellipses (max 7 items). */
function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

/**
 * ScanHistory
 * - Si `limit` est fourni (utilisation dashboard/widget) : mode compat rétro, pas de pagination.
 * - Sinon : pagination serveur (`page` + `page_size`, défaut 15).
 */
export default function ScanHistory({ source = null, limit = null, pageSize = 15, title = "Historique des scans" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const paginated = limit == null;

  const load = () => {
    setLoading(true);
    const params = paginated ? { page, page_size: pageSize } : { limit };
    if (source) params.source = source;
    return api.get("/scans/history", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, limit, page, pageSize]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const visibleIds = useMemo(() => items.map((s) => s.id), [items]);
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someChecked = visibleIds.some((id) => selected.has(id)) && !allChecked;

  const toggleAll = () => {
    setSelected((prev) => {
      if (allChecked) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectedCount = visibleIds.filter((id) => selected.has(id)).length;

  const doDelete = async () => {
    setDeleting(true);
    const ids = visibleIds.filter((id) => selected.has(id));
    try {
      const { data } = await api.post("/scans/delete", { ids });
      toast.success(`${data.deleted || 0} scan(s) retiré(s) de la liste.`);
      setSelected(new Set());
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error("Erreur lors de la suppression.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="hairline" data-testid="scan-history">
      <div className="p-5 border-b border-border flex items-center gap-3 flex-wrap">
        {items.length > 0 && (
          <Checkbox
            checked={allChecked ? true : someChecked ? "indeterminate" : false}
            onCheckedChange={toggleAll}
            aria-label="Tout sélectionner"
            data-testid="scan-history-select-all"
          />
        )}
        <History className="w-4 h-4 text-muted-foreground" />
        <p className="eyebrow">{title}</p>
        <span className="font-mono text-[11px] text-muted-foreground">{items.length}</span>
        <div className="ml-auto flex items-center gap-2">
          {selectedCount > 0 && (
            <span className="font-mono text-[11px] text-muted-foreground" data-testid="scan-history-selected-count">
              {selectedCount} sélectionné(s)
            </span>
          )}
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={selectedCount === 0 || deleting}
            onClick={() => setConfirmOpen(true)}
            data-testid="scan-history-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Supprimer la sélection
          </Button>
        </div>
      </div>

      {loading && <p className="p-8 text-center text-sm text-muted-foreground">Chargement…</p>}
      {!loading && items.length === 0 && (
        <p className="p-8 text-center text-sm text-muted-foreground" data-testid="scan-history-empty">
          Aucun scan pour le moment.
        </p>
      )}
      {items.map((s, i) => {
        const Icon = ICON[s.source] || Globe2;
        const isChecked = selected.has(s.id);
        return (
          <div
            key={s.id}
            className={`p-4 grid grid-cols-[auto_auto_1fr_auto_auto_auto] gap-4 items-center hover:bg-accent/40 transition ${i > 0 ? "border-t border-border" : ""} ${isChecked ? "bg-accent/30" : ""}`}
            data-testid={`scan-history-row-${s.id}`}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => toggle(s.id)}
              aria-label={`Sélectionner ${s.label}`}
              data-testid={`scan-history-check-${s.id}`}
            />
            <Icon className="w-4 h-4 text-muted-foreground" />
            <Link to={`/app/scan/${s.id}`} className="min-w-0 block" data-testid={`scan-history-link-${s.id}`}>
              <p className="font-serif text-sm truncate">{s.label}</p>
              <p className="font-mono text-[11px] text-muted-foreground">
                {TYPE_LABEL[s.source]} · {new Date(s.scanned_at).toLocaleString("fr-FR")} · <span className={s.verified_score != null ? "text-[hsl(var(--status-green))]" : ""}>{s.version_label || "v1"}</span>
              </p>
            </Link>
            <span className="tag-red px-2 py-0.5 rounded-lg text-[10px] font-mono">{s.red_count} RED</span>
            <span className="tag-orange px-2 py-0.5 rounded-lg text-[10px] font-mono">{s.orange_count} ORANGE</span>
            <Link to={`/app/scan/${s.id}`} aria-label="Ouvrir le scan"><ArrowRight className="w-4 h-4 text-muted-foreground" /></Link>
          </div>
        );
      })}

      {paginated && total > 0 && (
        <div className="p-4 border-t border-border flex items-center justify-between gap-3 flex-wrap" data-testid="scan-history-pagination">
          <p className="font-mono text-[11px] text-muted-foreground">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} sur <b className="text-foreground">{total}</b>
          </p>
          <div className="flex items-center gap-1">
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="scan-history-prev"
              aria-label="Page précédente"
              className="h-8 px-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {buildPageNumbers(page, pages).map((p, idx) =>
              p === "…" ? (
                <span key={`e${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
              ) : (
                <Button
                  key={p}
                  type="button"
                  variant={p === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(p)}
                  disabled={loading}
                  className="h-8 min-w-[32px] px-2 font-mono text-xs"
                  data-testid={`scan-history-page-${p}`}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages || loading}
              data-testid="scan-history-next"
              aria-label="Page suivante"
              className="h-8 px-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="scan-history-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedCount} scan(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les scans sélectionnés seront retirés de votre liste. Cette action est
              irréversible côté affichage : la donnée reste conservée en base et
              tracée dans votre journal d'audit. Les quotas consommés ne sont pas
              remboursés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="scan-history-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="scan-history-confirm-delete"
            >
              {deleting ? "Suppression…" : "Confirmer la suppression"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
